import React, { useState } from "react";
import { Checkbox } from "@/components/ui/checkbox";
import { api } from "@/lib/api";

export const fieldLabel = "block text-[10px] uppercase tracking-[0.24em] text-[#1c1713]/60 mb-2";
export const inputCls =
  "w-full bg-white/70 border border-[#1c1713]/20 focus:border-[#4B0F1E] outline-none px-4 py-3 text-sm font-light text-[#1c1713] placeholder:text-[#1c1713]/35 transition-colors duration-300";

export const TextField = ({ label, required, testId, textarea, ...props }) => (
  <div>
    <label className={fieldLabel}>
      {label}
      {required && <span className="text-[#4B0F1E]"> *</span>}
    </label>
    {textarea ? (
      <textarea data-testid={testId} required={required} rows={5} className={`${inputCls} resize-y`} {...props} />
    ) : (
      <input data-testid={testId} required={required} className={inputCls} {...props} />
    )}
  </div>
);

export const SelectField = ({ label, required, testId, options, ...props }) => (
  <div>
    <label className={fieldLabel}>
      {label}
      {required && <span className="text-[#4B0F1E]"> *</span>}
    </label>
    <select data-testid={testId} required={required} className={`${inputCls} appearance-none cursor-pointer`} {...props}>
      {options.map((o) => (
        <option key={o.value} value={o.value} disabled={o.disabled}>
          {o.label}
        </option>
      ))}
    </select>
  </div>
);

const ALLOWED_EXT = [".pdf", ".docx", ".jpg", ".jpeg", ".png", ".webp"];
const MAX_BYTES = 10 * 1024 * 1024;

export const FileField = ({ label, file, onChange, testId }) => {
  const [err, setErr] = useState("");
  const handle = (e) => {
    const f = e.target.files?.[0];
    if (!f) {
      onChange(null);
      setErr("");
      return;
    }
    const ext = "." + (f.name.split(".").pop() || "").toLowerCase();
    if (!ALLOWED_EXT.includes(ext)) {
      setErr("Accepted formats: PDF, DOCX, JPG, PNG, WEBP.");
      e.target.value = "";
      onChange(null);
      return;
    }
    if (f.size > MAX_BYTES) {
      setErr("File must be 10MB or smaller.");
      e.target.value = "";
      onChange(null);
      return;
    }
    setErr("");
    onChange(f);
  };
  return (
    <div>
      <label className={fieldLabel}>{label}</label>
      <input
        type="file"
        data-testid={testId}
        accept={ALLOWED_EXT.join(",")}
        onChange={handle}
        className="block w-full text-sm font-light text-[#1c1713]/70 file:mr-4 file:px-5 file:py-2.5 file:border file:border-[#1c1713]/30 file:bg-transparent file:text-[10px] file:uppercase file:tracking-[0.2em] file:text-[#1c1713] file:cursor-pointer hover:file:bg-[#1c1713] hover:file:text-[#F7F1E7] file:transition-colors"
      />
      <p className="mt-2 text-[11px] font-light text-[#1c1713]/45">PDF, DOCX, JPG, PNG or WEBP — max 10MB.</p>
      {err && (
        <p data-testid={`${testId}-error`} className="mt-1 text-[12px] text-[#4B0F1E]">
          {err}
        </p>
      )}
      {file && (
        <p data-testid={`${testId}-name`} className="mt-1 text-[12px] font-light text-[#1c1713]/70">
          Attached: {file.name}
        </p>
      )}
    </div>
  );
};

export const ConsentField = ({ checked, onChange, testId }) => (
  <label className="flex items-start gap-3 cursor-pointer max-w-xl">
    <Checkbox
      checked={checked}
      onCheckedChange={onChange}
      data-testid={testId}
      className="mt-0.5 border-[#1c1713]/40 data-[state=checked]:bg-[#4B0F1E] data-[state=checked]:border-[#4B0F1E]"
    />
    <span className="text-[12px] font-light leading-relaxed text-[#1c1713]/65">
      I confirm I am of legal drinking age in my jurisdiction and consent to Hema Connoisseur
      Collections storing the information above solely to respond to this enquiry.
    </span>
  </label>
);

export const SubmitButton = ({ sending, testId, children }) => (
  <button
    type="submit"
    data-testid={testId}
    disabled={sending}
    className="inline-block px-9 py-4 bg-[#1c1713] text-[#F7F1E7] text-[11px] uppercase tracking-[0.22em] font-semibold hover:bg-[#4B0F1E] transition-colors duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
  >
    {sending ? "Sending…" : children}
  </button>
);

export const SuccessNote = ({ testId }) => (
  <div data-testid={testId} className="max-w-xl border border-[#C7A45D]/50 bg-white/60 px-8 py-10">
    <div className="h-px w-14 bg-[#C7A45D]" />
    <h3 className="hcc-serif text-2xl text-[#1c1713] mt-6">Thank you — your enquiry has been received.</h3>
    <p className="mt-4 text-sm font-light leading-relaxed text-[#1c1713]/65">
      Our trade desk reviews every enquiry individually and will respond to you directly. We
      appreciate your interest in Hema Connoisseur Collections.
    </p>
  </div>
);

export const useEnquirySubmit = () => {
  const [status, setStatus] = useState("idle"); // idle | sending | success | error
  const [error, setError] = useState("");
  const submit = async (payload, file) => {
    setStatus("sending");
    setError("");
    try {
      const fd = new FormData();
      Object.entries(payload).forEach(([k, v]) => fd.append(k, v ?? ""));
      if (file) fd.append("file", file);
      await api.post("/enquiries", fd);
      setStatus("success");
    } catch (e) {
      setStatus("error");
      setError(e?.response?.data?.detail || "Something went wrong. Please try again.");
    }
  };
  return { status, error, submit };
};
